import React, { useEffect, useState, useCallback, useRef } from "react";
import { fetchUserAccounts, changeUserAccountStatus, addUserBalance } from "../services/api";

const ActionMenu = ({ item, onToggleStatus, togglingId, onAddBalance }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        disabled={togglingId === item.user_id}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: "4px",
          color: "#666",
          fontSize: "18px",
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Actions"
      >
        {togglingId === item.user_id ? "..." : "⋮"}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "6px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            zIndex: 100,
            minWidth: "150px",
            overflow: "hidden",
          }}
        >
          <button
            className="action-menu-item"
            onClick={() => {
              setOpen(false);
              onAddBalance(item);
            }}
          >
            Add Balance
          </button>
          <button
            className="action-menu-item action-menu-danger"
            onClick={() => {
              setOpen(false);
              onToggleStatus(item.user_id);
            }}
          >
            Deactivate
          </button>
        </div>
      )}
    </div>
  );
};

const UserAccount = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [togglingId, setTogglingId] = useState(null);

  const [balanceModal, setBalanceModal] = useState({ open: false, user: null });
  const [balanceForm, setBalanceForm] = useState({ amount: "", reason: "" });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceErrors, setBalanceErrors] = useState({});

  const [filters, setFilters] = useState({
    registration_number: "",
    assigned_did: "",
    status: "",
    page: 1,
    per_page: 10,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchUserAccounts(filters);
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      console.error("Failed to fetch user accounts:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value, page: 1 }));

  const handleReset = () =>
    setFilters({ registration_number: "", assigned_did: "", status: "", page: 1, per_page: 10 });

  const handlePageChange = (newPage) =>
    setFilters((prev) => ({ ...prev, page: newPage }));

  const handleToggleStatus = async (userId) => {
    const target = data.find((item) => item.user_id === userId);
    if (
      !window.confirm(
        `Deactivating ${target?.registration_number ?? "this user"} deletes the account. Continue?`
      )
    ) {
      return;
    }
    setTogglingId(userId);
    try {
      await changeUserAccountStatus(userId);
      setData((prev) =>
        prev.map((item) =>
          item.user_id === userId ? { ...item, status: !item.status } : item
        )
      );
    } catch (err) {
      console.error("Failed to toggle status:", err);
      window.alert(err.message || "Failed to change status");
    } finally {
      setTogglingId(null);
    }
  };

  const openBalanceModal = (user) => {
    setBalanceModal({ open: true, user });
    setBalanceForm({ amount: "", reason: "" });
    setBalanceErrors({});
  };

  const closeBalanceModal = () => {
    if (balanceLoading) return;
    setBalanceModal({ open: false, user: null });
    setBalanceForm({ amount: "", reason: "" });
    setBalanceErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setBalanceForm((prev) => ({ ...prev, [name]: value }));
    setBalanceErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errors = {};
    const amount = parseFloat(balanceForm.amount);
    if (!balanceForm.amount || isNaN(amount) || amount <= 0)
      errors.amount = "Enter a valid amount greater than 0.";
    if (!balanceForm.reason.trim())
      errors.reason = "Reason is required.";
    return errors;
  };

  const handleAddBalance = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) { setBalanceErrors(errors); return; }

    setBalanceLoading(true);
    try {
      const res = await addUserBalance(
        balanceModal.user.user_id,
        parseFloat(balanceForm.amount),
        balanceForm.reason.trim()
      );
      setData((prev) =>
        prev.map((item) =>
          item.user_id === balanceModal.user.user_id
            ? { ...item, balance: res.new_balance }
            : item
        )
      );
      closeBalanceModal();
    } catch (err) {
      setBalanceErrors({ general: err.message || "Failed to add balance. Please try again." });
    } finally {
      setBalanceLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h2 className="page-title">User Account</h2>

      <form className="filter-form" onSubmit={(e) => e.preventDefault()}>
        <input type="text" name="registration_number" value={filters.registration_number}
          onChange={handleFilterChange} className="filter-input" placeholder="Registration Number" />
        <input type="text" name="assigned_did" value={filters.assigned_did}
          onChange={handleFilterChange} className="filter-input" placeholder="Assigned DID" />
        <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-input">
          <option value="">All</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
        <button type="submit" className="filter-btn">Search</button>
        <button type="button" className="filter-btn reset-btn" onClick={handleReset}>Reset</button>
      </form>

      <div className="table-container">
        <table className="call-logs-table">
          <thead>
            <tr className="table-header-row">
              <th className="table-header">User ID</th>
              <th className="table-header">Registration Number</th>
              <th className="table-header">Balance</th>
              <th className="table-header">Assigned DID</th>
              <th className="table-header">Created At</th>
              <th className="table-header">Status</th>
              <th className="table-header">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No records found</td></tr>
            ) : (
              data.map((item) => (
                <tr key={item.user_id} className="table-row">
                  <td className="table-cell">{item.user_id || "N/A"}</td>
                  <td className="table-cell">{item.registration_number || "N/A"}</td>
                  <td className="table-cell">{item.balance?.toFixed(2) ?? "0.00"}</td>
                  <td className="table-cell">{item.assigned_did || "N/A"}</td>
                  <td className="table-cell">{item.created_at ?? "N/A"}</td>
                  <td className="table-cell">
                    <span className={`status-badge ${item.status == 1 ? "active" : "inactive"}`}>
                      {item.status == 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="table-cell">
                    {item.status == 1 ? (
                      <ActionMenu
                        item={item}
                        togglingId={togglingId}
                        onToggleStatus={handleToggleStatus}
                        onAddBalance={openBalanceModal}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total_items > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.current_page - 1) * filters.per_page) + 1}–{Math.min(pagination.current_page * filters.per_page, pagination.total_items)} of {pagination.total_items}{' '}
            accounts (Page {pagination.current_page} of {pagination.total_pages})
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={!pagination.has_prev}
              className={`pagination-btn ${!pagination.has_prev ? 'disabled' : ''}`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={!pagination.has_next}
              className={`pagination-btn ${!pagination.has_next ? 'disabled' : ''}`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Balance Modal */}
      {balanceModal.open && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={closeBalanceModal}>
          <div style={{ background: "#fff", borderRadius: "8px", padding: "28px 32px",
            minWidth: "380px", maxWidth: "460px", width: "100%",
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: "4px" }}>Add Balance</h3>
            <p style={{ marginBottom: "20px", color: "#666", fontSize: "14px" }}>
              User: <strong>{balanceModal.user?.user_id}</strong>
              &nbsp;|&nbsp;
              Current Balance: <strong>{balanceModal.user?.balance?.toFixed(2) ?? "0.00"}</strong>
            </p>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                Amount <span style={{ color: "red" }}>*</span>
              </label>
              <input type="number" name="amount" min="0.01" step="0.01"
                placeholder="Enter amount" value={balanceForm.amount}
                onChange={handleFormChange} className="filter-input"
                style={{ width: "100%" }} autoFocus />
              {balanceErrors.amount && (
                <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{balanceErrors.amount}</p>
              )}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                Reason <span style={{ color: "red" }}>*</span>
              </label>
              <textarea name="reason" placeholder="Enter reason for recharge..."
                value={balanceForm.reason} onChange={handleFormChange}
                className="filter-input" rows={3}
                style={{ width: "100%", resize: "vertical" }} />
              {balanceErrors.reason && (
                <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{balanceErrors.reason}</p>
              )}
            </div>

            {balanceErrors.general && (
              <p style={{ color: "red", fontSize: "13px", marginBottom: "12px" }}>{balanceErrors.general}</p>
            )}

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" }}>
              <button className="filter-btn reset-btn" onClick={closeBalanceModal} disabled={balanceLoading}>
                Cancel
              </button>
              <button className="filter-btn" onClick={handleAddBalance} disabled={balanceLoading}>
                {balanceLoading ? "Adding..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccount;